"use strict";
/**
 * Novig venue API — every client call that ultimately hits Novig.
 *
 * READ PATH: listNovigEvents() → GET /novig/events
 *   → server src/server/routes/novig/markets.ts
 *   → src/server/novig/client.ts → Novig REST API.
 *
 * ORDER PATH: placeNovigOrder(), below.
 *
 * Response shapes come from `src/shared/novig.ts` — the same declarations the
 * server projects into, so the two cannot drift.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NovigUnavailableError = exports.NovigNotConfiguredError = void 0;
exports.translateNovigError = translateNovigError;
exports.listNovigLeagues = listNovigLeagues;
exports.listNovigEvents = listNovigEvents;
exports.placeNovigOrder = placeNovigOrder;
exports.getNovigEventMarkets = getNovigEventMarkets;
const request_1 = require("./request");
const operator_error_1 = require("./operator-error");
/**
 * The server has no Novig credentials, so it never mounted the `/novig` router
 * and every path under it 404s.
 *
 * This is a configuration state, not a failure: the operator should be told
 * Novig isn't set up on this server, not shown a generic "please try again".
 * Keeping the router unmounted (rather than adding an availability endpoint)
 * means an unconfigured deploy still exposes no `/novig` path at all
 * (GOTCHAS §10) — its absence IS the signal.
 */
class NovigNotConfiguredError extends operator_error_1.OperatorFacingError {
    constructor() {
        super("Novig is not configured on this server");
        this.name = "NovigNotConfiguredError";
    }
}
exports.NovigNotConfiguredError = NovigNotConfiguredError;
/**
 * Novig refused or did not answer, and the server said why in words meant for
 * the operator — e.g. `NOVIG_GEOLOCATION`: "Open the Novig app on your phone."
 */
class NovigUnavailableError extends operator_error_1.OperatorFacingError {
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "NovigUnavailableError";
    }
}
exports.NovigUnavailableError = NovigUnavailableError;
function isNovigErrorBody(data) {
    const body = data;
    return (typeof body?.code === "string" &&
        body.code.startsWith("NOVIG_") &&
        typeof body.error === "string");
}
/**
 * Translate a failed `/novig` call: the unmounted-router 404 and the server's
 * `NOVIG_*` failures become errors whose message is written for the operator.
 * Anything else is returned unchanged.
 *
 * A 404 on a `/novig` path means the router isn't there, except where a route
 * uses 404 for "not ready yet" (the order book) — that caller checks first.
 */
function translateNovigError(error) {
    if (!(error instanceof request_1.ApiRequestError))
        return error;
    if (error.statusCode === 404)
        return new NovigNotConfiguredError();
    if (isNovigErrorBody(error.responseData)) {
        return new NovigUnavailableError(error.responseData.code, error.responseData.error);
    }
    return error;
}
async function novigCall(path, params) {
    try {
        return await (0, request_1.apiRequest)("GET", path, params ? { params } : {});
    }
    catch (error) {
        throw translateNovigError(error);
    }
}
/** Novig's league vocabulary, e.g. `["NFL", "NBA", "MLB"]`. */
async function listNovigLeagues() {
    const response = await novigCall("/novig/leagues");
    return response.leagues ?? [];
}
/**
 * Open events for a league — upcoming and live, live first.
 *
 * No `status` is sent: the server defaults to both open statuses.
 */
async function listNovigEvents(league) {
    const response = await novigCall("/novig/events", league ? { league } : undefined);
    return response.events ?? [];
}
/**
 * ORDER PATH (hot): placeNovigOrder() → POST /novig/orders
 *   → server src/server/routes/novig/orders.ts → novig/orders/place.ts → Novig.
 *
 * One await, nothing else: the build before it is synchronous
 * (orders/novig-creation.ts). A refusal comes back as a NovigUnavailableError
 * whose message says what to do ("fund the subaccount", "open the Novig app").
 */
async function placeNovigOrder(request) {
    try {
        return await (0, request_1.apiRequest)("POST", "/novig/orders", { body: request });
    }
    catch (error) {
        throw translateNovigError(error);
    }
}
/** One event's markets. No prices: the live book is the only Novig price (S2.3). */
async function getNovigEventMarkets(eventId) {
    const response = await novigCall(`/novig/events/${encodeURIComponent(eventId)}/markets`);
    return response.markets ?? [];
}
